import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSocket } from '../contexts/SocketContext';
import type { Team } from '../types';

export default function TeamPlay() {
  const { gameId, teamId } = useParams();
  const navigate = useNavigate();
  const { 
    game, 
    activeQuestion, 
    timeRemaining, 
    questionResults,
    finalResults,
    isConnected,
    joinGame,
    submitAnswer,
    voteMol,
    error 
  } = useSocket();

  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [molVote, setMolVote] = useState<string | null>(null);
  const [hasVoted, setHasVoted] = useState(false);

  // Get our team
  const team: Team | undefined = game?.teams.find(t => t.id === teamId);

  // Reset answer state when new question appears
  useEffect(() => {
    if (activeQuestion) {
      setSelectedAnswer(null);
      setHasAnswered(false);
    }
  }, [activeQuestion?.question.id]);

  // Check if we already answered this question
  useEffect(() => {
    if (team && activeQuestion) {
      const answered = team.answers.find(a => a.questionId === activeQuestion.question.id);
      if (answered) {
        setHasAnswered(true);
        setSelectedAnswer(answered.answer);
      }
    }
  }, [team, activeQuestion]);

  // Reconnect if needed
  useEffect(() => {
    if (isConnected && gameId && !game) {
      // Try to reconnect using stored team info
      const stored = localStorage.getItem('widm-team');
      if (stored) {
        const { teamName } = JSON.parse(stored);
        if (teamName) {
          // We need to rejoin - but the server should remember us
          // For now, just wait for game state
        }
      }
    }
  }, [isConnected, gameId, game]);

  const handleSelectAnswer = (answer: string) => {
    if (hasAnswered) return;
    setSelectedAnswer(answer);
  };

  const handleSubmitAnswer = () => {
    if (!gameId || !teamId || !activeQuestion || !selectedAnswer || hasAnswered) return;
    
    submitAnswer(gameId, teamId, activeQuestion.question.id, selectedAnswer);
    setHasAnswered(true);
  };

  const handleVoteMol = () => {
    if (!gameId || !teamId || !molVote || hasVoted) return;
    
    voteMol(gameId, teamId, molVote);
    setHasVoted(true);
  };

  // Not connected or no game
  if (!game || !team) {
    return (
      <div className="container flex-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <h2 className="mb-2">Verbinden...</h2>
          <p className="text-muted">
            {!isConnected ? 'Verbinden met server...' : 'Spel laden...'}
          </p>
          <button 
            className="btn btn-secondary mt-2"
            onClick={() => navigate('/join')}
          >
            Opnieuw aanmelden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '20px', paddingBottom: '20px', minHeight: '100vh' }}>
      {/* Header */}
      <div className="card mb-2">
        <div className="flex-between">
          <div>
            <h3 style={{ color: 'var(--color-primary)' }}>{team.name}</h3>
            <p className="text-muted" style={{ fontSize: '0.875rem' }}>
              {team.players.join(', ')}
            </p>
          </div>
          <div className="score-badge" style={{ fontSize: '1.25rem' }}>
            {team.score} pts
          </div>
        </div>
      </div>

      {error && (
        <div className="card mb-2 animate-shake" style={{ background: 'var(--color-error)', padding: '12px' }}>
          {error}
        </div>
      )}

      {/* Lobby */}
      {game.status === 'lobby' && (
        <div className="card text-center">
          <h2 className="mb-2">⏳ Wachten op start...</h2>
          <p className="text-muted">
            De spelleider start het spel zodra alle teams klaar zijn.
          </p>
          <p className="mt-2">
            {game.teams.length} team{game.teams.length !== 1 ? 's' : ''} aangemeld
          </p>
        </div>
      )}

      {/* Active Question */}
      {game.status === 'playing' && activeQuestion && (
        <div className="animate-fadeIn">
          {/* Timer */}
          <div className="text-center mb-2">
            <span className={`timer ${timeRemaining <= 5 ? 'warning' : ''}`}>
              {timeRemaining}s
            </span>
          </div>

          {/* Hint */}
          {activeQuestion.displayedHint && (
            <div className="hint-box">
              💡 {activeQuestion.displayedHint}
            </div>
          )}

          {/* Question */}
          <div className="card mb-2">
            <p style={{ fontSize: '1.25rem', fontWeight: 600, textAlign: 'center' }}>
              {activeQuestion.question.text}
            </p>
          </div>

          {/* Options */}
          {!hasAnswered ? (
            <>
              <div className="flex gap-1" style={{ flexDirection: 'column' }}>
                {activeQuestion.question.options.map((option, index) => {
                  const isHidden = activeQuestion.hiddenOptions.includes(index);
                  return (
                    <button
                      key={index}
                      className={`option-btn ${selectedAnswer === option ? 'selected' : ''} ${isHidden ? 'hidden' : ''}`}
                      onClick={() => handleSelectAnswer(option)}
                      disabled={isHidden}
                      style={{ textAlign: 'left' }}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>

              <button
                className="btn btn-primary btn-large mt-2"
                style={{ width: '100%' }}
                onClick={handleSubmitAnswer}
                disabled={!selectedAnswer}
              >
                ✓ Bevestig Antwoord
              </button>
            </>
          ) : (
            <div className="card text-center" style={{ background: 'var(--color-success)' }}>
              <h3>✅ Antwoord verstuurd!</h3>
              <p className="mt-1">Jullie kozen: {selectedAnswer}</p>
            </div>
          )}
        </div>
      )}

      {/* Question Results */}
      {game.status === 'playing' && questionResults && (
        <div className="card text-center animate-fadeIn">
          <h3 className="mb-2">Resultaat</h3>
          
          {(() => {
            const ourResult = questionResults.teamResults.find(r => r.teamId === teamId);
            return ourResult ? (
              <div 
                className="card mb-2" 
                style={{ 
                  background: ourResult.correct ? 'var(--color-success)' : 'var(--color-error)',
                  padding: '16px'
                }}
              >
                <p style={{ fontSize: '1.5rem', fontWeight: 700 }}>
                  {ourResult.correct ? '✅ CORRECT!' : '❌ Helaas...'}
                </p>
                <p className="mt-1">
                  {ourResult.correct 
                    ? `+${ourResult.points} punten!` 
                    : `Het juiste antwoord was: ${questionResults.correctAnswer}`
                  }
                </p>
              </div>
            ) : null;
          })()}
          
          <p className="text-muted">Wachten op volgende vraag...</p>
        </div>
      )}

      {/* Waiting between questions */}
      {game.status === 'playing' && !activeQuestion && !questionResults && (
        <div className="card text-center">
          <h3>⏳ Volgende vraag...</h3>
          <p className="text-muted mt-1">Wacht op de spelleider</p>
        </div>
      )}

      {/* Voting */}
      {game.status === 'voting' && !hasVoted && (
        <div className="animate-fadeIn">
          <div className="card mb-2 text-center">
            <h2 style={{ color: 'var(--color-primary)' }}>🔍 Wie is de Mol?</h2>
            <p className="text-muted mt-1">
              Raad correct voor {game.settings.molVoteBonus} bonuspunten!
            </p>
          </div>

          <div className="flex gap-1" style={{ flexDirection: 'column' }}>
            <button
              className={`option-btn ${molVote === 'spelleider' ? 'selected' : ''}`}
              onClick={() => setMolVote('spelleider')}
            >
              🎭 De Spelleider
            </button>
          </div>

          <button
            className="btn btn-accent btn-large mt-2"
            style={{ width: '100%' }}
            onClick={handleVoteMol}
            disabled={!molVote}
          >
            🗳️ Stem!
          </button>
        </div>
      )}

      {/* Voted */}
      {game.status === 'voting' && hasVoted && (
        <div className="card text-center">
          <h3>✅ Stem verstuurd!</h3>
          <p className="text-muted mt-1">Wachten op andere teams...</p>
        </div>
      )}

      {/* Final Results */}
      {game.status === 'finished' && finalResults && (
        <div className="animate-fadeIn">
          <div className="card mb-2 text-center" style={{ background: 'var(--color-primary)' }}>
            <h2>🦊 De Mol was: De Spelleider!</h2>
          </div>

          {(() => {
            const ourResult = finalResults.teams.find(t => t.teamId === teamId);
            const ourRank = finalResults.teams.findIndex(t => t.teamId === teamId) + 1;
            const isWinner = ourRank === 1;
            
            return (
              <div 
                className="card text-center mb-2"
                style={{ background: isWinner ? 'var(--color-accent)' : 'var(--color-bg-light)' }}
              >
                <h2 style={{ color: isWinner ? 'var(--color-bg)' : 'inherit' }}>
                  {isWinner ? '🏆 GEWONNEN!' : `#${ourRank} - ${ourResult?.totalScore} punten`}
                </h2>
                {ourResult?.molBonus && ourResult.molBonus > 0 && (
                  <p className="mt-1">+{ourResult.molBonus} Mol bonus!</p>
                )}
              </div>
            );
          })()}

          <div className="scoreboard">
            {finalResults.teams.map((t, index) => (
              <div 
                key={t.teamId}
                className={`scoreboard-item ${index === 0 ? 'winner' : ''} ${t.teamId === teamId ? 'animate-pulse' : ''}`}
              >
                <div className="scoreboard-rank">#{index + 1}</div>
                <div className="scoreboard-team">
                  {t.teamName}
                  {t.teamId === teamId && ' (jullie)'}
                </div>
                <div className="scoreboard-score">{t.totalScore}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
